import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Layout, Typography, Button, Table, Tag, message, Space, Select, Alert, Modal
} from 'antd';
import {
  DeleteOutlined, ReloadOutlined, CheckOutlined, MailOutlined, StopOutlined, PhoneOutlined
} from '@ant-design/icons';
import Navigation from '../components/Navigation';
import axios from 'axios';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const API = 'https://limitverify.ru:5000/api/account-deletions';

const statusColor = {
  pending: 'default',
  awaiting_client: 'processing',
  client_confirmed: 'warning',
  completed: 'success',
  cancelled: 'error'
};

const statusLabel = {
  pending: 'Новая',
  awaiting_client: 'Ждёт подтверждения клиента',
  client_confirmed: 'Можно удалять',
  completed: 'Выполнено',
  cancelled: 'Отменено'
};

const AccountDeletions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, {
        headers,
        params: { status: statusFilter }
      });
      if (res.data.success) setRows(res.data.requests || []);
    } catch (e) {
      message.error(e.response?.data?.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const act = async (id, action, okMsg) => {
    try {
      const res = await axios.post(`${API}/${id}/${action}`, {}, { headers });
      message.success(okMsg + (res.data?.to ? ` → ${res.data.to}` : ''));
      load();
    } catch (e) {
      const details = e.response?.data?.details;
      message.error(
        (e.response?.data?.error || 'Ошибка')
          + (details ? `: ${typeof details === 'string' ? details : JSON.stringify(details)}` : '')
      );
    }
  };

  const confirmPhone = (id) => {
    Modal.confirm({
      title: 'Подтвердить за клиента?',
      content: 'Используйте после звонка/переписки, когда клиент устно подтвердил удаление. Код из письма не нужен.',
      okText: 'Да, подтверждено',
      cancelText: 'Отмена',
      onOk: () => act(id, 'confirm-by-admin', 'Подтверждено менеджером')
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70
    },
    {
      title: 'Тикет',
      dataIndex: 'ticketId',
      width: 90,
      render: (id) => (id ? <Link to={`/tickets/${id}`}>#{id}</Link> : '—')
    },
    {
      title: 'Клиент',
      key: 'client',
      render: (_, r) => (
        <div>
          <div>{r.contactName || r.client?.fullName || '—'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.contactEmail || r.client?.email || '—'}
            {(r.contactPhone || r.client?.phone) ? ` · ${r.contactPhone || r.client?.phone}` : ''}
          </Text>
        </div>
      )
    },
    {
      title: 'Опции',
      key: 'opts',
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.keepNameInHistory ? <div>Имя в истории</div> : <div>Имя обезличить</div>}
          {r.keepPublicPhotos ? <div>Фото оставить</div> : <div>Фото удалить</div>}
        </div>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (s) => <Tag color={statusColor[s] || 'default'}>{statusLabel[s] || s}</Tag>
    },
    {
      title: 'Создана',
      dataIndex: 'createdAt',
      render: (v) => (v ? new Date(v).toLocaleString('ru-RU') : '—')
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, r) => (
        <Space wrap>
          <Button size="small" icon={<CheckOutlined />} onClick={() => act(r.id, 'contacted', 'Отмечено: связались')}>
            Связались
          </Button>
          {['pending', 'awaiting_client'].includes(r.status) && (
            <>
              <Button size="small" icon={<MailOutlined />} onClick={() => act(r.id, 'resend-confirm', 'Письмо клиенту')}>
                Письмо ещё раз
              </Button>
              <Button size="small" type="primary" icon={<PhoneOutlined />} onClick={() => confirmPhone(r.id)}>
                Подтверждено клиентом
              </Button>
            </>
          )}
          {r.status === 'client_confirmed' && (
            <Button
              size="small"
              danger
              type="primary"
              icon={<DeleteOutlined />}
              onClick={() => act(r.id, 'execute', 'Аккаунт удалён')}
            >
              Выполнить удаление
            </Button>
          )}
          {!['completed', 'cancelled'].includes(r.status) && (
            <Button size="small" icon={<StopOutlined />} onClick={() => act(r.id, 'cancel', 'Отменено')}>
              Отмена
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Navigation />
      <Content style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
          <Title level={3} style={{ margin: 0 }}>Удаление аккаунтов</Title>
          <Space>
            <Select
              style={{ width: 220 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'Все' },
                { value: 'awaiting_client', label: 'Ждут подтверждения' },
                { value: 'client_confirmed', label: 'Можно удалять' },
                { value: 'completed', label: 'Выполнены' },
                { value: 'cancelled', label: 'Отменены' }
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
          </Space>
        </div>

        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Как работать с заявкой"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              1) Клиент подаёт заявку → появляется тикет и эта строка.<br />
              2) Свяжитесь с клиентом. Код из письма вводит <b>клиент</b> у себя в профиле (или по ссылке) — менеджер код не вводит.<br />
              3) Если связались по телефону — нажмите «Подтверждено клиентом».<br />
              4) Затем «Выполнить удаление».
            </Paragraph>
          }
        />

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
        />
      </Content>
    </Layout>
  );
};

export default AccountDeletions;
